// CSV Validation utilities to prevent wrong file type uploads

export interface CSVValidationResult {
  valid: boolean;
  fileType: "earnings" | "expenses" | "unknown";
  missingColumns: string[];
  message: string;
}

// Columns unique to earnings CSV (Turo export)
const EARNINGS_REQUIRED_COLUMNS = ["reservation id", "vehicle", "vin", "total earnings"];
const EARNINGS_IDENTIFIER_COLUMNS = ["trip start", "trip end", "trip status", "trip days"];

// Columns unique to expenses CSV
const EXPENSES_REQUIRED_COLUMNS = ["date", "expense", "expense type"];
const EXPENSES_AMOUNT_COLUMNS = ["amount", "miles driven"];

/**
 * Validates that CSV headers match the expected earnings format
 */
export function validateEarningsCSV(headers: string[]): CSVValidationResult {
  const normalizedHeaders = headers.map((h) => h.toLowerCase().trim());
  const missingColumns: string[] = [];

  // Check for required earnings columns
  for (const required of EARNINGS_REQUIRED_COLUMNS) {
    if (!normalizedHeaders.includes(required)) {
      missingColumns.push(required);
    }
  }

  // Check if this looks like an expenses CSV (wrong file type)
  const hasExpenseColumns = EXPENSES_REQUIRED_COLUMNS.every((col) =>
    normalizedHeaders.includes(col)
  );
  const hasEarningsIdentifiers = EARNINGS_IDENTIFIER_COLUMNS.some((col) =>
    normalizedHeaders.includes(col)
  );

  if (hasExpenseColumns && !hasEarningsIdentifiers) {
    return {
      valid: false,
      fileType: "expenses",
      missingColumns: [],
      message:
        "This looks like an expenses CSV file. Please upload it on the Expenses page instead.",
    };
  }

  if (missingColumns.length > 0) {
    return {
      valid: false,
      fileType: "unknown",
      missingColumns,
      message: `Missing required columns: ${missingColumns.join(", ")}. Please upload a Turo earnings CSV export.`,
    };
  }

  return {
    valid: true,
    fileType: "earnings",
    missingColumns: [],
    message: "Valid earnings CSV",
  };
}

/**
 * Validates that CSV headers match the expected expenses format
 */
export function validateExpensesCSV(headers: string[]): CSVValidationResult {
  const normalizedHeaders = headers.map((h) => h.toLowerCase().trim());
  const missingColumns: string[] = [];

  // Check for required expense columns
  for (const required of EXPENSES_REQUIRED_COLUMNS) {
    if (!normalizedHeaders.includes(required)) {
      missingColumns.push(required);
    }
  }

  // Check that at least one of Amount or Miles Driven is present
  const hasAmount = normalizedHeaders.includes("amount");
  const hasMileage = normalizedHeaders.includes("miles driven");

  if (!hasAmount && !hasMileage) {
    missingColumns.push("Amount or Miles Driven (at least one required)");
  }

  // Check if this looks like an earnings CSV (wrong file type)
  const hasEarningsColumns = EARNINGS_REQUIRED_COLUMNS.every((col) =>
    normalizedHeaders.includes(col)
  );

  if (hasEarningsColumns) {
    return {
      valid: false,
      fileType: "earnings",
      missingColumns: [],
      message:
        "This looks like a Turo earnings CSV file. Please upload it on the Earnings page instead.",
    };
  }

  if (missingColumns.length > 0) {
    return {
      valid: false,
      fileType: "unknown",
      missingColumns,
      message: `Missing required columns: ${missingColumns.join(", ")}`,
    };
  }

  return {
    valid: true,
    fileType: "expenses",
    missingColumns: [],
    message: "Valid expenses CSV",
  };
}
